import { useMemo } from 'react';
import {
  PaymentPlanDetailsHistoryResponse,
  PaymentPlanInvoiceStatus,
  PaymentPlanStatus,
  getPaymentPlanDetails,
  getPaymentPlanHistory,
} from '@frontend/api-payment-plans';
import { useScopedQuery } from '@frontend/scope';
import { paymentPlanQueryKeys } from '../../utils/query-keys';

export const usePaymentPlanDetails = ({ paymentPlanId }: { paymentPlanId: string }) => {
  const {
    data: paymentPlan,
    refetch: refetchDetails,
    isFetched,
  } = useScopedQuery({
    enabled: !!paymentPlanId,
    queryKey: paymentPlanQueryKeys.details(paymentPlanId),
    queryFn: () => getPaymentPlanDetails(paymentPlanId),
    onError: (err) => console.error('detailsError: ', err),
  });

  const { data: planHistory, refetch: refetchHistory } = useScopedQuery({
    enabled: !!paymentPlanId,
    queryKey: paymentPlanQueryKeys.history(paymentPlanId),
    queryFn: () => getPaymentPlanHistory(paymentPlanId),
    onError: (err) => console.error('historyError: ', err),
    select: (data) => ({ ...data, planHistory: data.planHistory.map((historyItem) => historyItem) }),
  });

  const paymentPlanHistory: PaymentPlanDetailsHistoryResponse | undefined = useMemo(
    () =>
      planHistory && {
        ...planHistory,
        planHistory: planHistory?.planHistory.map((historyItem) => {
          if (historyItem.payment && historyItem.payment?.status === PaymentPlanInvoiceStatus.Scheduled) {
            if (paymentPlan?.status === PaymentPlanStatus.Canceled) {
              historyItem.payment.status = PaymentPlanInvoiceStatus.Canceled;
            } else if (
              paymentPlan?.status === PaymentPlanStatus.Paused ||
              paymentPlan?.status === PaymentPlanStatus.Failed
            ) {
              historyItem.payment.status = PaymentPlanInvoiceStatus.Paused;
            }
          }
          return historyItem;
        }),
      },
    [planHistory, paymentPlan, isFetched]
  );

  const refetch = async () => {
    await Promise.allSettled([refetchDetails(), refetchHistory()]);
  };

  return {
    paymentPlanId,
    paymentPlan,
    refetchDetails,
    planHistory: paymentPlanHistory,
    refetchHistory,
    refetch,
  };
};
